(() => {
  if (window.becomeLasioProInitialized) return;
  window.becomeLasioProInitialized = true;

  function initialize(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanup = [];
    const controller = new AbortController();
    const signal = controller.signal;

    // Swiper is already provided by the theme layout. Keep each reference's breakpoints.
    root.querySelectorAll('[data-become-type="slider"]').forEach((element) => {
      const config = root.querySelector(`#instant-slider-${element.id}-params`);
      if (!config || !window.Swiper) return;
      const params = JSON.parse(config.textContent);
      if (reducedMotion.matches) {
        Object.values(params.breakpoints).forEach((breakpoint) => { breakpoint.speed = 0; });
      }
      if (params.navigation) {
        params.navigation.nextEl = root.querySelector(params.navigation.nextEl);
        params.navigation.prevEl = root.querySelector(params.navigation.prevEl);
      }
      if (params.pagination) {
        params.pagination.el = root.querySelector(params.pagination.el);
        const bulletClass = params.pagination.el?.firstElementChild?.className || '';
        params.pagination.renderBullet = (index, className) =>
          `<button type="button" class="${className} ${bulletClass}" aria-label="Go to slide ${index + 1}"></button>`;
      }
      params.a11y = { enabled: true };
      const slider = new window.Swiper(element, params);
      cleanup.push(() => slider.destroy(true, true));
    });

    const reveals = root.querySelectorAll('.instant-scroll-trigger');
    if (!reducedMotion.matches && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          target.classList.remove('instant-scroll-trigger--hidden');
          observer.unobserve(target);
        });
      }, { threshold: 0.05 });
      reveals.forEach((element) => {
        element.classList.add('instant-scroll-trigger--hidden');
        observer.observe(element);
      });
      cleanup.push(() => observer.disconnect());
    }

    root.querySelectorAll('[data-become-type="tabs-container"]').forEach((container, group) => {
      const menu = container.querySelector('[data-become-type="tabs-menu"]');
      const tabs = [...menu.querySelectorAll('[data-become-type="tabs-trigger"]')];
      const panels = [...container.querySelectorAll('[data-become-type="tabs-pane"]')];
      menu.setAttribute('role', 'tablist');
      menu.setAttribute('aria-label', 'Keratin formulas');
      const activate = (selected, focus = false) => {
        tabs.forEach((tab, index) => {
          const active = index === selected;
          const panel = panels.find((item) => item.dataset.becomePanelId === tab.dataset.becomeTabId);
          tab.dataset.becomeState = active ? 'active' : 'inactive';
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (panel) {
            panel.dataset.becomeState = active ? 'open' : 'closed';
            panel.hidden = !active;
            panel.style.removeProperty('display');
          }
        });
        if (focus) {
          tabs[selected].focus({ preventScroll: true });
          tabs[selected].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }
      };
      tabs.forEach((tab, index) => {
        const panel = panels.find((item) => item.id === tab.dataset.becomeTabId);
        const prefix = 'BecomeProFormula-' + root.dataset.sectionId + '-' + group + '-' + index;
        tab.id = prefix + '-tab';
        tab.setAttribute('role', 'tab');
        if (panel) {
          panel.dataset.becomePanelId = panel.id;
          panel.id = prefix + '-panel';
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
          panel.tabIndex = 0;
          tab.setAttribute('aria-controls', panel.id);
        }
        tab.addEventListener('click', () => activate(index), { signal });
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault();
          activate(next, true);
        }, { signal });
      });
      const initial = tabs.findIndex((tab) => tab.dataset.becomeState === 'active');
      activate(Math.max(initial, 0));
    });

    root.querySelectorAll('[data-become-type="ticker"]').forEach((ticker) => {
      const tracks = ticker.querySelectorAll(':scope > .instant-ticker');
      const original = tracks[0].firstElementChild;
      const populate = () => {
        tracks.forEach((track) => track.replaceChildren());
        tracks[0].append(original);
        const count = Math.max(1, Math.ceil(ticker.clientWidth / Math.max(original.offsetWidth, 1)));
        for (let index = 1; index < count; index++) {
          const copy = original.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          tracks[0].append(copy);
        }
        tracks[1].innerHTML = tracks[0].innerHTML;
        tracks[1].setAttribute('aria-hidden', 'true');
        const gap = parseFloat(getComputedStyle(ticker).columnGap) || 0;
        ticker.style.setProperty('--gap', `${gap}px`);
        ticker.style.setProperty('--duration', `${(tracks[0].offsetWidth + gap) / Number(ticker.dataset.becomeTickerSpeed || 20)}s`);
        ticker.style.setProperty('--direction', 'normal');
        ticker.style.setProperty('--play', 'running');
      };
      populate();
      document.fonts.ready.then(() => { if (root.isConnected) populate(); });
      const observer = new ResizeObserver(populate);
      observer.observe(ticker);
      cleanup.push(() => observer.disconnect());
    });

    root.querySelectorAll('[data-become-type="accordion-container"]').forEach((container, group) => {
      const items = [...container.querySelectorAll('[data-become-type="accordion-item"]')];
      const multiple = container.dataset.isMultiOpenEnabled === 'true';
      const updates = [];
      items.forEach((item, index) => {
        const trigger = item.querySelector('[data-become-type="accordion-header"]');
        const content = item.querySelector('[data-become-type="accordion-content"]');
        const id = 'BecomeProFAQ-' + root.dataset.sectionId + '-' + group + '-' + index;
        trigger.id = id + '-trigger';
        content.id = id + '-content';
        trigger.setAttribute('aria-controls', content.id);
        content.setAttribute('aria-labelledby', trigger.id);
        item.dataset.state = index === 0 && container.dataset.isFirstOpen === 'true' ? 'open' : 'closed';
        const update = () => {
          const open = item.dataset.state === 'open';
          trigger.setAttribute('aria-expanded', String(open));
          content.inert = !open;
          content.setAttribute('aria-hidden', String(!open));
          content.style.setProperty('--instant-accordion-content-height', content.scrollHeight + 'px');
        };
        updates.push(update);
        trigger.addEventListener('click', () => {
          const opening = item.dataset.state !== 'open';
          if (opening && !multiple) items.forEach((other) => { other.dataset.state = 'closed'; });
          item.dataset.state = opening ? 'open' : 'closed';
          updates.forEach((refresh) => refresh());
        }, { signal });
        update();
        const observer = new ResizeObserver(update);
        Array.from(content.children).forEach((child) => observer.observe(child));
        cleanup.push(() => observer.disconnect());
      });
    });

    const header = document.querySelector('sticky-header')?.closest('.section-header');
    const updateHeaderOffset = () => {
      const height = header?.getBoundingClientRect().height || 0;
      root.style.setProperty('--become-header-offset', `${height}px`);
      return height;
    };
    updateHeaderOffset();
    if (header) {
      const observer = new ResizeObserver(updateHeaderOffset);
      observer.observe(header);
      cleanup.push(() => observer.disconnect());
    }

    root.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href^="#"]');
      if (!anchor || anchor.hash.length < 2) return;
      const target = document.getElementById(anchor.hash.slice(1));
      if (!target || !root.contains(target)) return;
      event.preventDefault();
      const offset = updateHeaderOffset();
      const top = window.scrollY + target.getBoundingClientRect().top - offset;
      window.scrollTo({ top, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      history.replaceState(null, '', anchor.hash);
    }, { signal });

    root.addEventListener('become-a-lasio-pro:unload', () => {
      controller.abort();
      cleanup.forEach((dispose) => dispose());
    }, { once: true });
  }

  const initializeAll = () => document.querySelectorAll('[data-become-a-lasio-pro]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-become-a-lasio-pro]')?.dispatchEvent(new Event('become-a-lasio-pro:unload'));
  });
})();
